import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import "../config.styles.css";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import BrowseS3List from "./browseS3";
import PVCPackageLogsTable, {
    PACKAGE_LOG_TYPE,
    PACKAGE_VENDOR_OPTIONS,
    TRANSFER_VENDOR_OPTIONS,
    VENDOR_TRANSFER_LOG_TYPE,
} from "./pvcPackageLogsTable";
import ProxyLogsTable from "./proxyLogsTable";

import { ENVIRONMENTS } from "../LandingPage/constants";
import ValidationLogsTable from "./validationLogsTable";
import { useSearchParams } from "react-router-dom";
import WaivableErrorsDialog from "../../UI/atoms/Popup/WaivableErrorsDialog";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const IngestionUpload = () => {
    // hooks
    const { enqueueSnackbar } = useSnackbar();

    const [fileName, setFileName] = useState("");
    const [selectedStudio, setSelectedStudio] = useState("");
    const [submitting, setSubmitting] = useState(true);
    // const [value, setValue] = React.useState(0);
    const pvcTableRef = useRef<any>(null);
    const vendorTransferTableRef = useRef<any>(null);
    const validationTableRef = useRef<any>(null);
    const proxyLogsTableRef = useRef<any>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const value = searchParams.get("tab")
        ? parseInt(searchParams.get("tab") || "0")
        : 0;

    const [loader, setLoader] = useState(false);
    const [waivableErrorsDialogOpen, setWaivableErrorsDialogOpen] =
        useState(false);
    const [waivableErrorsData, setWaivableErrorsData] = useState<{
        errors: string[];
        action: "Ingest";
    } | null>(null);
    const [pendingIngestionParams, setPendingIngestionParams] = useState<{
        studioName: string;
        fileNameValue: string;
        ingestMethod: string;
        currents3Env: string;
    } | null>(null);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setSearchParams({ tab: newValue.toString() });
        // setValue(newValue);
    };

    // API call for ingestion
    const ingestionStart = async (
        studioName: string = selectedStudio,
        fileNameValue: string = fileName,
        ingestMethod: string = "",
        currents3Env: string = ENVIRONMENTS.DEV,
        waiveErrors: boolean = false
    ) => {
        try {
            if (studioName && fileNameValue) {
                const xlsxFileName: any = fileNameValue.includes("xlsx")
                    ? fileNameValue
                    : fileNameValue + ".xlsx";
                setSubmitting(false);
                const url = `${
                    process.env.BASE_URL
                }api/v1/cms/ingest?studioName=${encodeURIComponent(
                    studioName
                )}&fileName=${encodeURIComponent(
                    xlsxFileName
                )}&ingestType=${encodeURIComponent(
                    ingestMethod
                )}&s3Env=${encodeURIComponent(currents3Env)}${
                    waiveErrors ? "&waiveErrors=true" : ""
                }`;
                const ingestionAPIResponse = await axios.get(url);

                if (
                    ingestionAPIResponse.data?.errorCode === "WAIVABLE_ERRORS"
                ) {
                    // Store the parameters for retry with waiveErrors
                    setPendingIngestionParams({
                        studioName,
                        fileNameValue,
                        ingestMethod,
                        currents3Env,
                    });
                    // Transform allErrors array of objects to array of formatted strings
                    const formattedErrors = (
                        ingestionAPIResponse.data?.data?.allErrors || []
                    ).map((errorItem: any) => {
                        if (typeof errorItem === "string") {
                            return errorItem;
                        }
                        const errorMessages = Array.isArray(
                            errorItem.errorMessage
                        )
                            ? errorItem.errorMessage.join(", ")
                            : errorItem.errorMessage || "";
                        return errorItem.rowNo
                            ? `Row ${errorItem.rowNo}: ${errorMessages}`
                            : errorMessages;
                    });
                    setWaivableErrorsData({
                        errors: formattedErrors,
                        action: "Ingest",
                    });
                    setWaivableErrorsDialogOpen(true);
                } else if (ingestionAPIResponse.data?.success) {
                    enqueueSnackbar(ingestionAPIResponse.data?.message, {
                        variant: "success",
                    });
                    clearInputValues();
                } else {
                    enqueueSnackbar(ingestionAPIResponse.data?.message, {
                        variant: "error",
                    });
                }
            } else {
                enqueueSnackbar("Studio Name & File Name are mandatory", {
                    variant: "warning",
                });
            }
        } catch (error: any) {
            if (error?.response?.data?.errorCode === "WAIVABLE_ERRORS") {
                // Store the parameters for retry with waiveErrors
                setPendingIngestionParams({
                    studioName,
                    fileNameValue,
                    ingestMethod,
                    currents3Env,
                });
                // Transform allErrors array of objects to array of formatted strings
                const formattedErrors = (
                    error.response.data?.data?.allErrors || []
                ).map((errorItem: any) => {
                    if (typeof errorItem === "string") {
                        return errorItem;
                    }
                    const errorMessages = Array.isArray(errorItem.errorMessage)
                        ? errorItem.errorMessage.join(", ")
                        : errorItem.errorMessage || "";
                    return errorItem.rowNo
                        ? `Row ${errorItem.rowNo}: ${errorMessages}`
                        : errorMessages;
                });
                setWaivableErrorsData({
                    errors: formattedErrors,
                    action: "Ingest",
                });
                setWaivableErrorsDialogOpen(true);
            } else {
                enqueueSnackbar(
                    error?.response?.data?.message || "An error occurred",
                    { variant: "error" }
                );
            }
        } finally {
            setSubmitting(true);
        }
    };

    const handleWaiveErrors = () => {
        if (pendingIngestionParams) {
            setWaivableErrorsDialogOpen(false);
            ingestionStart(
                pendingIngestionParams.studioName,
                pendingIngestionParams.fileNameValue,
                pendingIngestionParams.ingestMethod,
                pendingIngestionParams.currents3Env,
                true // waiveErrors = true
            );
            setPendingIngestionParams(null);
        }
    };

    const handleCloseWaivableErrorsDialog = () => {
        setWaivableErrorsDialogOpen(false);
        setWaivableErrorsData(null);
        setPendingIngestionParams(null);
    };

    const clearInputValues = () => {
        setSelectedStudio("");
        setFileName("");
    };

    const handlePVCRefresh = () => {
        pvcTableRef.current.getIngestionLogsList();
    };

    const handleVendorTransferRefresh = () => {
        vendorTransferTableRef.current.getIngestionLogsList();
    };

    const handleValidationRefresh = () => {
        validationTableRef.current.getIngestionLogsList();
    };

    const handleProxyLogsRefresh = () => {
        proxyLogsTableRef.current?.getProxyLogsList();
    };

    const handleLoader = (value: boolean) => {
        setLoader(value);
    };

    return (
        <div className="config-heading">
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                    sx={{
                        "& .MuiTab-root": {
                            fontWeight: "bold",
                        },
                    }}
                >
                    <Tab label="Browse S3" {...a11yProps(0)} />
                    <Tab label="Validation Logs" {...a11yProps(1)} />
                    <Tab label="Packages Logs" {...a11yProps(2)} />
                    <Tab label="Vendor Transfer Logs" {...a11yProps(3)} />
                    <Tab label="Proxy Logs" {...a11yProps(4)} />
                </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
                <BrowseS3List ingestionStart={ingestionStart} />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                <div className="">
                    <div className="">
                        <div>
                            <div className="ingest-header-refresh">
                                <h3>Validation List</h3>
                                {loader ? (
                                    <CircularProgress
                                        size={20}
                                        color="primary"
                                        className="ml-1"
                                    />
                                ) : (
                                    <RefreshIcon
                                        className="action-icons"
                                        onClick={handleValidationRefresh}
                                    />
                                )}
                            </div>
                            <ValidationLogsTable
                                ref={validationTableRef}
                                handleLoader={handleLoader}
                            />
                        </div>
                    </div>
                </div>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <div className="">
                    <div className="">
                        <div>
                            <div className="ingest-header-refresh">
                                <h3>Package List</h3>
                                {loader ? (
                                    <CircularProgress
                                        size={20}
                                        color="primary"
                                        className="ml-1"
                                    />
                                ) : (
                                    <RefreshIcon
                                        className="action-icons"
                                        onClick={handlePVCRefresh}
                                    />
                                )}
                            </div>
                            <PVCPackageLogsTable
                                ref={pvcTableRef}
                                handleLoader={handleLoader}
                                logType={PACKAGE_LOG_TYPE}
                                detailPath="/pvc-package-logs"
                                vendorOptions={PACKAGE_VENDOR_OPTIONS}
                            />
                        </div>
                    </div>
                </div>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <div className="">
                    <div className="">
                        <div>
                            <div className="ingest-header-refresh">
                                <h3>Vendor Transfer List</h3>
                                {loader ? (
                                    <CircularProgress
                                        size={20}
                                        color="primary"
                                        className="ml-1"
                                    />
                                ) : (
<Button
    aria-label="Refresh vendor transfer logs"
    className="action-icons"
    onClick={handleVendorTransferRefresh}
>
    <RefreshIcon />
</Button>
                                )}
                            </div>
                            <PVCPackageLogsTable
                                ref={vendorTransferTableRef}
                                handleLoader={handleLoader}
                                logType={VENDOR_TRANSFER_LOG_TYPE}
                                detailPath="/vendor-transfer-logs"
                                vendorOptions={TRANSFER_VENDOR_OPTIONS}
                            />
                        </div>
                    </div>
                </div>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
                <div className="">
                    <div className="">
                        <div>
                            <div className="ingest-header-refresh">
                                <h3>Proxy Logs</h3>
                                {loader ? (
                                    <CircularProgress
                                        size={20}
                                        color="primary"
                                        className="ml-1"
                                    />
                                ) : (
                                    <RefreshIcon
                                        className="action-icons"
                                        onClick={handleProxyLogsRefresh}
                                    />
                                )}
                            </div>
                            <ProxyLogsTable
                                ref={proxyLogsTableRef}
                                handleLoader={handleLoader}
                            />
                        </div>
                    </div>
                </div>
            </CustomTabPanel>
            {waivableErrorsData && (
                <WaivableErrorsDialog
                    isOpen={waivableErrorsDialogOpen}
                    errors={waivableErrorsData.errors}
                    action={waivableErrorsData.action}
                    onClose={handleCloseWaivableErrorsDialog}
                    onWaive={handleWaiveErrors}
                />
            )}
        </div>
    );
};

export default IngestionUpload;
